/**
 * useLongPress — a press held for `holdMs` without moving (PR-4, 2026-09-09).
 * Pointer events only, so touch, pen and mouse share one path. Cancelled by a
 * release before the hold, a move beyond `moveTolerancePx`, or pointercancel.
 * When the hold fires, the trailing click the browser emits on release is
 * swallowed once so a long-press never also "clicks" the element beneath it.
 *
 * Returns handlers to spread onto the element; `holdMs` 0 disables.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

export const LONG_PRESS_HOLD_MS = 450;
export const LONG_PRESS_MOVE_TOLERANCE_PX = 8;

export interface UseLongPressArgs {
  onLongPress: (point: { x: number; y: number; target: EventTarget | null }) => void;
  holdMs?: number;
  moveTolerancePx?: number;
  disabled?: boolean;
}

export interface LongPressHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  onClickCapture: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

export function useLongPress(args: UseLongPressArgs): LongPressHandlers {
  const { holdMs = LONG_PRESS_HOLD_MS, moveTolerancePx = LONG_PRESS_MOVE_TOLERANCE_PX, disabled = false } = args;
  const onLongPressRef = useRef(args.onLongPress);
  onLongPressRef.current = args.onLongPress;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originRef = useRef<{ x: number; y: number; target: EventTarget | null } | null>(null);
  const firedRef = useRef(false);
  const swallowClickRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    originRef.current = null;
  }, []);

  useEffect(() => clear, [clear]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (disabled || holdMs <= 0) return;
    if (e.button !== undefined && e.button !== 0) return; // primary button / touch only
    clear();
    firedRef.current = false;
    const origin = { x: e.clientX, y: e.clientY, target: e.target };
    originRef.current = origin;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (!originRef.current) return;
      firedRef.current = true;
      swallowClickRef.current = true;
      try {
        onLongPressRef.current(origin);
      } catch {
        /* a failing receiver never breaks the press */
      }
    }, holdMs);
  }, [clear, disabled, holdMs]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const origin = originRef.current;
    if (!origin || timerRef.current === null) return;
    const dx = e.clientX - origin.x;
    const dy = e.clientY - origin.y;
    if (Math.sqrt(dx * dx + dy * dy) > moveTolerancePx) clear();
  }, [clear, moveTolerancePx]);

  const onPointerUp = useCallback(() => { clear(); }, [clear]);
  const onPointerCancel = useCallback(() => { clear(); }, [clear]);

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (!swallowClickRef.current) return;
    swallowClickRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // A long touch also raises contextmenu on some platforms — keep the picker
  // the only reaction to it once the hold has fired.
  const onContextMenu = useCallback((e: React.MouseEvent) => {
    if (firedRef.current) e.preventDefault();
  }, []);

  return useMemo(() => ({ onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClickCapture, onContextMenu }),
    [onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClickCapture, onContextMenu]);
}
